import { useEffect, useState } from 'react';
import { api, type AccountPackage, type Framework } from './api.ts';
import './users.css';

/**
 * What this account bought, and how much of it is left.
 *
 * A panel inside Settings, and the reason it exists is narrow: for a while
 * every one of these numbers was visible to DriftWatch's own staff on the
 * platform account screen and to nobody in the console. The first time a
 * customer learned their package allowed four clients was a refusal on the
 * fifth, a form's worth of typing into onboarding one. A limit somebody can
 * plan around is a commercial term; the same limit discovered by being refused
 * is a fault in the product.
 *
 * So it is deliberately plain: used against allowed, in the same words the
 * refusals use, with nothing to click. Changing a package is a conversation
 * with us. What each client may have — frameworks and assessments — is bought
 * per client on its Plan tab and shown above this panel under Billing; the two
 * rows here that say so are the account-wide defaults a client without a plan
 * falls back to.
 *
 * Offered to every role. Knowing that four of five seats are taken is what
 * stops an engineer inviting somebody who cannot be admitted, and it is a fact
 * about their own account rather than a setting they are being trusted with.
 */

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

/** Null is unlimited, everywhere in a package. Never rendered as a blank. */
const limit = (value: number | null): string => (value === null ? 'Unlimited' : String(value));

const overLimit = (used: number, allowed: number | null): boolean =>
  allowed !== null && used > allowed;

export const Package = () => {
  const [detail, setDetail] = useState<AccountPackage | null>(null);
  const [catalogue, setCatalogue] = useState<readonly Framework[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .accountPackage()
      .then(setDetail)
      .catch((cause: unknown) => setError(message(cause)));
    /*
     * Only to turn `cis-v8` into the name the framework calls itself. A failure
     * here is not worth an error banner — the ids are still readable, and the
     * numbers above them are what this panel is for.
     */
    void api
      .listFrameworks()
      .then((response) => setCatalogue(response.frameworks))
      .catch(() => undefined);
  }, []);

  if (error !== null) return <div className="error us-error">{error}</div>;
  if (detail === null) return <p className="note">Loading package…</p>;

  const { allowance, usage } = detail;
  const rows: ReadonlyArray<{ label: string; used: number; allowed: number | null }> = [
    { label: 'Connected clients', used: usage.tenants, allowed: allowance.maxTenants },
    {
      label: 'Clients with scheduled assessment on',
      used: usage.schedules,
      allowed: allowance.maxSchedules,
    },
    { label: 'Active operators', used: usage.seats, allowed: allowance.maxSeats },
  ];

  const frameworks =
    allowance.frameworks === null
      ? null
      : allowance.frameworks.map(
          (id) => catalogue.find((framework) => framework.id === id)?.name ?? id,
        );

  const stopped = rows.some((row) => overLimit(row.used, row.allowed));

  return (
    <section className="us-panel" aria-labelledby="package-heading">
      <h2 id="package-heading">Your package</h2>

      <p className="lead">
        {detail.packageName === null ? (
          <>
            No package has been agreed for this account yet, so nothing is capped. Everything below
            is unlimited until one is.
          </>
        ) : (
          <>
            This account is on <strong>{detail.packageName}</strong>. To change any of it, get in
            touch — there is nothing to click here.
          </>
        )}
      </p>

      {/*
        Over a limit is not the same as at one, and only the first stops the
        sweep. It happens when a package changes under an estate that has
        already grown past it, and a customer whose assessments have stopped
        deserves to be told where the numbers are rather than left to work it
        out from a schedule screen full of skipped clients.
      */}
      {stopped && (
        <div className="callout callout-caution">
          <span className="callout-title">Assessments are stopped</span>
          This account is over one of the limits below, so none of its clients is being assessed
          until the package or the estate changes. Everything already collected is untouched.
        </div>
      )}

      <div className="tbl-wrap us-wrap">
        <table className="tbl us-table">
          <thead>
            <tr>
              <th scope="col">Allowance</th>
              <th scope="col">Used</th>
              <th scope="col">Allowed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.used}</td>
                <td>{limit(row.allowed)}</td>
              </tr>
            ))}
            {/*
              Per client and per calendar month, so there is no single "used" to
              put beside it: one client may have spent the month's assessments
              while another has not started. When a client is due again is on
              that client's own screen.
            */}
            {/*
              Defaults only. A client with its own plan is held to that plan's
              numbers instead, which is why these two rows say "unless".
            */}
            <tr>
              <td>Assessments per client per month, unless the client’s plan says otherwise</td>
              <td>—</td>
              <td>{limit(allowance.maxScansPerTenantPeriod)}</td>
            </tr>
            <tr>
              <td>Frameworks reports may cover, unless the client’s plan says otherwise</td>
              <td>—</td>
              <td>{frameworks === null ? 'All' : frameworks.join(', ')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="note">
        Every control is assessed for every client whatever the package says. A framework outside it
        is a projection we do not print, never a check we did not make.
      </p>
    </section>
  );
};
